/**
 * Pestaña «Tareas» de la ficha (REQ-046): vista por defecto de tareas abiertas (completadas ocultas, con histórico
 * al marcar «Mostrar completadas»), con tiempo previsto / planificado / real por tarea.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ProcessChip } from '../ProcessPicker';
import { TaskCode } from '../TaskCode';
import { useNavigate } from 'react-router-dom';
import { nexusTaskUrl } from '../useNexusAccess';
import {
  workApi, errMsg, fmtDate, fmtMinutes, PRIORITY_LABELS, SOURCE_LABELS, TASK_STATUS_LABELS,
  type ProcessList, type ProjectDetail, type TaskPriority, type TaskRow, type TaskStatus,
} from '../../../stores/workApi';
import { Icon, Modal, Field, PriorityLabel, ErrorBanner, inputCls, btnGhost, btnPrimary } from '../WorkUi';
import { deviationText, fromLocalInput, plannedRangeError, timingStateText, toLocalInput, type TimingTone } from '../../../utils/fichaTiming';

const TONE_CLS: Record<TimingTone, string> = { muted: 'text-outline', normal: 'text-on-surface-variant', warn: 'text-[#F59E0B]', danger: 'text-[#F43F5E]' };
const selCls = 'rounded-lg border border-outline-variant bg-surface-container-high px-2 py-1 text-xs text-on-surface';

export function TaskStatusSelect({ task, onChange }: { task: TaskRow; onChange: (s: TaskStatus) => void }) {
  if (!task.capabilities.changeStatus) return null;
  return (
    <select aria-label={`Estado de ${task.title}`} value={task.status} onChange={(e) => onChange(e.target.value as TaskStatus)} className={selCls}>
      {(['TODO', 'IN_PROGRESS', 'BLOCKED', 'IN_REVIEW', 'DONE'] as TaskStatus[]).map((s) => <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>)}
    </select>
  );
}

function TimingCell({ t }: { t: TaskRow }) {
  const tm = t.timing;
  if (!tm) return <span className="text-xs text-outline">—</span>;
  const st = timingStateText(tm);
  const dev = deviationText(tm);
  return (
    <div className="text-xs" data-testid="task-timing">
      <p className="text-on-surface">
        <span data-testid="timing-estimated">{tm.estimatedMinutes ? fmtMinutes(tm.estimatedMinutes) : '—'}</span> previsto · <span data-testid="timing-actual">{tm.actualMinutes ? fmtMinutes(tm.actualMinutes) : '—'}</span> real
      </p>
      <p className={TONE_CLS[st.tone]} data-testid="timing-state">{st.text}</p>
      {dev && <p className="text-[#F59E0B]" data-testid="timing-deviation">{dev}{!tm.deviationCause ? ' · falta la causa' : ''}</p>}
      {dev && tm.deviationCause && <p className="text-outline" data-testid="timing-cause">Causa: {tm.deviationCause}</p>}
    </div>
  );
}

/** Tiempo previsto, planificación y (con la tarea cerrada) causa de la desviación y resultado. */
export function TaskTimingModal({ task, onClose, onSaved }: { task: TaskRow; onClose: () => void; onSaved: () => void }) {
  const tm = task.timing;
  const [estimate, setEstimate] = useState(tm?.estimatedMinutes ? String(tm.estimatedMinutes) : '');
  const [start, setStart] = useState(toLocalInput(tm?.plannedStart));
  const [end, setEnd] = useState(toLocalInput(tm?.plannedEnd));
  const [cause, setCause] = useState(tm?.deviationCause ?? '');
  const [result, setResult] = useState(tm?.resultNote ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const done = task.status === 'DONE';
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const rangeError = plannedRangeError(start, end);
    if (rangeError) { setError(rangeError); return; }
    setBusy(true); setError(null);
    try {
      await workApi.updateTask(task.id, {
        estimatedMinutes: estimate ? Number(estimate) : null,
        plannedStart: fromLocalInput(start),
        plannedEnd: fromLocalInput(end),
        ...(done ? { deviationCause: cause.trim() || null, resultNote: result.trim() || null } : {}),
      });
      onSaved();
    } catch (err) { setError(errMsg(err, 'No se pudo guardar la planificación')); setBusy(false); }
  };
  const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('es-ES') : '—');
  return (
    <Modal title={`Tiempo y planificación · ${task.title}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="timing-form">
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Tiempo previsto (min)">
            <input type="number" min={1} max={10080} className={inputCls} placeholder="Sin estimación" value={estimate} onChange={(e) => setEstimate(e.target.value)} data-testid="tm-estimate" />
          </Field>
          <Field label="Inicio planificado"><input type="datetime-local" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} data-testid="tm-start" /></Field>
          <Field label="Fin planificado"><input type="datetime-local" className={inputCls} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="tm-end" /></Field>
        </div>
        <dl className="grid grid-cols-2 gap-2 text-xs text-on-surface-variant md:grid-cols-4">
          <div><dt className="uppercase tracking-wider">Fecha límite</dt><dd className="text-on-surface">{tm?.deadline ?? '—'}</dd></div>
          <div><dt className="uppercase tracking-wider">Inicio real</dt><dd className="text-on-surface">{when(tm?.actualStart)}</dd></div>
          <div><dt className="uppercase tracking-wider">Fin real</dt><dd className="text-on-surface">{when(tm?.actualEnd)}</dd></div>
          <div><dt className="uppercase tracking-wider">Duración real</dt><dd className="text-on-surface">{tm?.actualMinutes ? fmtMinutes(tm.actualMinutes) : '—'}</dd></div>
        </dl>
        {done && (
          <>
            <Field label={`Causa de la desviación${tm?.relevantDeviation ? ' (recomendada: la diferencia es relevante)' : ''}`}>
              <textarea className={`${inputCls} min-h-[64px]`} maxLength={1000} value={cause} onChange={(e) => setCause(e.target.value)} data-testid="tm-cause" />
            </Field>
            <Field label="Resultado"><textarea className={`${inputCls} min-h-[64px]`} maxLength={2000} value={result} onChange={(e) => setResult(e.target.value)} data-testid="tm-result" /></Field>
          </>
        )}
        <p className="text-xs text-outline">Al alcanzar el tiempo previsto solo se avisa; la tarea nunca se finaliza sola.</p>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <div className="flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
          <button type="submit" className={btnPrimary} disabled={busy} data-testid="tm-save"><Icon name="save" className="text-base" />Guardar</button>
        </div>
      </form>
    </Modal>
  );
}

export function FichaTareas({ project, tasks, showClosed, onShowClosed, nexusOk, act, onTimerStarted, onPlanSaved, onOpenTask }: {
  project: ProjectDetail; tasks: TaskRow[]; showClosed: boolean; onShowClosed: (v: boolean) => void; nexusOk: boolean; onOpenTask?: (taskId: string) => void;
  act: (fn: () => Promise<unknown>, ok?: string) => Promise<void>; onTimerStarted: () => void; onPlanSaved: () => void;
}) {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<TaskRow | null>(null);
  const [procs, setProcs] = useState<ProcessList | null>(null);
  const [newProc, setNewProc] = useState('');
  const [procErr, setProcErr] = useState<string | null>(null);
  const loadProcs = useCallback(async () => { try { setProcs(await workApi.processes(project.id, true)); } catch { setProcs(null); } }, [project.id]);
  useEffect(() => { void loadProcs(); }, [loadProcs, tasks.length]);
  const c = project.capabilities;
  const activeProcs = (procs?.processes ?? []).filter((p) => p.status === 'ACTIVE');
  const addProc = async () => {
    if (!newProc.trim()) return;
    try { await workApi.createProcess(project.id, { name: newProc.trim() }); setNewProc(''); setProcErr(null); await loadProcs(); }
    catch (e) { setProcErr(errMsg(e, 'No se pudo crear el proceso')); }
  };
  return (
    <section className="space-y-3">
      <div className="rounded-xl border border-outline-variant p-3" data-testid="processes-panel">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-on-surface">Procesos del proyecto</h3>
          {procs && procs.pendingClassification > 0 && <span className="text-xs text-[#B45309] dark:text-[#FBBF24]" data-testid="processes-pending">{procs.pendingClassification} tarea(s) pendientes de clasificar</span>}
        </div>
        <ul className="mt-2 flex flex-wrap gap-2">
          {(procs?.processes ?? []).map((p) => (
            <li key={p.id} className={`flex items-center gap-1.5 rounded-full border border-outline-variant px-2.5 py-1 text-xs ${p.status === 'ARCHIVED' ? 'opacity-60' : ''}`} data-testid="process-item">
              <span className="text-on-surface">{p.name}</span><span className="text-outline">{p.openTaskCount}/{p.taskCount}</span>
              {c.edit && <button type="button" className="text-outline hover:text-on-surface" title={p.status === 'ACTIVE' ? 'Archivar proceso' : 'Reactivar proceso'} onClick={() => act(async () => { await workApi.updateProcess(project.id, p.id, { status: p.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE' }); await loadProcs(); })}>{p.status === 'ACTIVE' ? '⤓' : '↺'}</button>}
            </li>
          ))}
          {procs && procs.processes.length === 0 && <li className="text-xs text-outline">Aún no hay procesos. Crea el primero para poder crear tareas.</li>}
        </ul>
        {c.createTask && (
          <div className="mt-2 flex flex-wrap gap-2">
            <input className={`${inputCls} min-w-0 flex-1`} placeholder="Nuevo proceso (p. ej. SEO mensual)" maxLength={200} value={newProc} onChange={(e) => setNewProc(e.target.value)} data-testid="process-name-input" />
            <button type="button" className={btnGhost} onClick={addProc} data-testid="process-create">+ Añadir proceso</button>
          </div>
        )}
        {procErr && <p className="mt-1 text-xs text-[#F87171]" role="alert">{procErr}</p>}
      </div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-on-surface">Tareas</h2>
        <label className="flex items-center gap-2 text-sm text-on-surface-variant"><input type="checkbox" checked={showClosed} onChange={(e) => onShowClosed(e.target.checked)} />Mostrar completadas</label>
      </div>
      <div className="overflow-x-auto rounded-xl border border-outline-variant">
        <table className="w-full text-sm">
          <thead className="bg-surface-container-high/60 text-left text-[11px] uppercase tracking-wider text-on-surface-variant">
            <tr>
              <th className="px-4 py-3">Tarea</th><th className="px-4 py-3">Prioridad</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Asignado</th>
              <th className="px-4 py-3">Fecha límite</th><th className="px-4 py-3">Tiempo</th><th className="px-4 py-3">Origen</th><th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-on-surface-variant">No hay tareas{showClosed ? '' : ' abiertas'}.</td></tr>}
            {tasks.map((t) => (
              <tr key={t.id} className="border-t border-outline-variant/60">
                <td className="px-4 py-3">
                  {onOpenTask
                    ? <button type="button" className="text-left font-medium text-on-surface hover:text-[#FF5E1E]" data-testid="task-open" onClick={() => onOpenTask(t.id)}><TaskCode code={t.taskCode} />{t.title}</button>
                    : <p className="font-medium text-on-surface"><TaskCode code={t.taskCode} />{t.title}</p>}{t.syncError && <p className="text-xs text-[#F43F5E]">Sincronización: {t.syncError}</p>}
                  <div className="mt-1 flex flex-wrap items-center gap-1.5"><ProcessChip process={t.process} />
                    {!t.process && t.capabilities.edit && activeProcs.length > 0 && (
                      <select aria-label={`Clasificar proceso de ${t.title}`} className={selCls} defaultValue="" onChange={(e) => e.target.value && act(async () => { await workApi.updateTask(t.id, { processId: e.target.value }); await loadProcs(); })} data-testid="task-classify">
                        <option value="">Clasificar…</option>{activeProcs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    )}
                  </div>
                  {t.capabilities.edit && <label className="mt-1 flex items-center gap-1 text-[11px] text-outline"><input type="checkbox" checked={!!t.guestVisible} onChange={(e) => act(() => workApi.setTaskGuestVisible(t.id, e.target.checked))} data-testid="task-guest-visible" />Visible para invitados externos</label>}</td>
                <td className="px-4 py-3">
                  {t.capabilities.edit ? (
                    <select aria-label={`Prioridad de ${t.title}`} className={selCls} value={t.priority} onChange={(e) => act(() => workApi.updateTask(t.id, { priority: e.target.value as TaskPriority }))}>
                      {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  ) : <PriorityLabel priority={t.priority} />}
                </td>
                <td className="px-4 py-3"><TaskStatusSelect task={t} onChange={(s) => act(() => workApi.updateTask(t.id, { status: s }), 'Estado actualizado')} />{!t.capabilities.changeStatus && TASK_STATUS_LABELS[t.status]}</td>
                <td className="px-4 py-3">
                  {t.capabilities.assign ? (
                    <select aria-label={`Asignar ${t.title}`} className={selCls} value={t.assignee?.id ?? ''} onChange={(e) => act(() => workApi.assignTask(t.id, e.target.value || null), 'Asignación actualizada')}>
                      <option value="">Sin asignar</option>
                      {project.members.filter((m) => m.accessStatus === 'ACTIVE').map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                      {t.assignee && !project.members.some((m) => m.userId === t.assignee!.id) && <option value={t.assignee.id}>{t.assignee.name}</option>}
                    </select>
                  ) : <span className="text-on-surface-variant">{t.assignee?.name ?? t.externalAssigneeName ?? '—'}</span>}
                </td>
                <td className="px-4 py-3 text-on-surface-variant">{fmtDate(t.dueDate)}</td>
                <td className="px-4 py-3"><TimingCell t={t} /></td>
                <td className="px-4 py-3 text-xs text-on-surface-variant">{SOURCE_LABELS[t.source] ?? t.source}{t.linkedAsana && t.source !== 'ASANA' ? ' + Asana' : ''}</td>
                <td className="px-4 py-3 text-right">
                  {onOpenTask && (
                    <button className="mr-2 inline-flex items-center gap-1 rounded-lg border border-outline-variant px-2 py-1 text-xs text-on-surface-variant hover:bg-surface-container-highest" data-testid="task-comments" onClick={() => onOpenTask(t.id)}>
                      <Icon name="forum" className="text-sm" />Abrir
                    </button>
                  )}
                  {(t.capabilities.edit || t.capabilities.changeStatus) && (
                    <button className="mr-2 inline-flex items-center gap-1 rounded-lg border border-outline-variant px-2 py-1 text-xs text-on-surface-variant hover:bg-surface-container-highest" data-testid="task-plan" onClick={() => setPlan(t)}>
                      <Icon name="schedule" className="text-sm" />Tiempo
                    </button>
                  )}
                  {nexusOk && project.status !== 'ARCHIVED' && (
                    <button className="mr-2 inline-flex items-center gap-1 rounded-lg bg-[#FF5E1E] px-2 py-1 text-xs text-white hover:bg-[#FF5E1E]/90" onClick={() => navigate(nexusTaskUrl(project.id, t.id))}>
                      <Icon name="bolt" className="text-sm" />Resolver con Nexus
                    </button>
                  )}
                  {c.logTime && project.status !== 'ARCHIVED' && (
                    <button className="inline-flex items-center gap-1 rounded-lg border border-[#FF5E1E]/40 px-2 py-1 text-xs text-[#FF5E1E] hover:bg-[#FF5E1E]/10"
                      onClick={() => act(async () => { await workApi.startTimer(project.id, t.id); onTimerStarted(); }, 'Temporizador iniciado')}>
                      <Icon name="play_arrow" className="text-sm" />Iniciar tiempo
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {plan && <TaskTimingModal task={plan} onClose={() => setPlan(null)} onSaved={() => { setPlan(null); onPlanSaved(); }} />}
    </section>
  );
}
